import {
	ExclamationCircleFilled,
	LoadingOutlined,
	MinusCircleOutlined,
	PlusOutlined,
	RedoOutlined,
} from "@ant-design/icons";
import type { Dispatch } from "@reduxjs/toolkit";
import type { FormInstance } from "antd";
import { Button, Form, Input, Select, Spin, Tag, Typography } from "antd";
import type { AnyObject } from "antd/es/_util/type";
import { connect, type DispatchProp } from "react-redux";
import { actions as routerActions } from "redux-router5";
import type { State as RouterState } from "router5";
import ConnectedAppLayout from "../AppLayout.tsx";
import type { RootState } from "../store.ts";
import * as commonColumns from "../../common/columns.tsx";
import { Card } from "../../common/components/Card.tsx";
import { DrawerItemWrapper } from "../../common/components/DrawerItemWrapper.tsx";
import { ListActionBar } from "../../common/components/ListActionBar.tsx";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import {
	TableWrapper,
	type PaginationAndFilter,
	type TableWrapperProps,
} from "../../common/components/Table.tsx";
import { userManagementFilter } from "../common/filter.ts";
import { sanitizedFilter } from "../../common/functions.ts";
import { isAdministrator } from "../../common/utils/isAdministrator.ts";
import { routeNames } from "../common/routes.ts";
import { userManagementActions } from "../../features/user-management/userManagementSlice.ts";
import type {
	PaginatedUserList,
	User,
	UserGroup,
} from "../generated-axios-client/index.ts";

const TECHNICIAN_LEVEL_COLOR: Record<string, string> = {
	L1: "blue",
	L2: "gold",
	L3: "green",
};

const onFilter =
	(dispatch: Dispatch, form: FormInstance, currentRoute: RouterState) => () => {
		form
			.validateFields()
			.then((values) => {
				dispatch(
					routerActions.navigateTo(
						currentRoute.name,
						sanitizedFilter({ ...currentRoute.params, ...values, page: 1 }),
					),
				);
			})
			.catch((error) => {
				console.log("Form validation error:", error);
			});
	};

const onCancel =
	(dispatch: Dispatch, form: FormInstance, currentRoute: RouterState) => () => {
		dispatch(
			routerActions.navigateTo(
				currentRoute.name,
				sanitizedFilter({
					...currentRoute.params,
					...userManagementFilter,
					page: 1,
				}),
			),
		);
		form.resetFields();
	};

const makeGetColumns =
	(
		isAdministratorViewing: boolean,
	): TableWrapperProps<PaginatedUserList, AnyObject>["getColumns"] =>
	(intl, currentFilter) => [
		commonColumns.simpleColumn(
			"row_number",
			"table_column_ticket_number",
			intl,
		),
		commonColumns.simpleColumn(
			"username",
			"table_column_username",
			intl,
			currentFilter,
		),
		commonColumns.simpleColumn(
			"full_name",
			"table_column_full_name",
			intl,
			currentFilter,
		),
		commonColumns.simpleColumn(
			"email",
			"table_column_email",
			intl,
			currentFilter,
		),
		commonColumns.simpleColumn(
			"phone_number",
			"table_column_phone_number",
			intl,
			currentFilter,
		),
		commonColumns.simpleColumn("groups", "table_column_groups", intl),
		commonColumns.simpleColumn(
			"technician_level",
			"table_column_technician_level",
			intl,
			currentFilter,
		),
		...(isAdministratorViewing
			? [
					commonColumns.simpleColumn(
						"is_active",
						"table_column_is_active",
						intl,
					),
					commonColumns.simpleColumn(
						"last_login",
						"table_column_last_login",
						intl,
					),
					commonColumns.simpleColumn("actions", "table_column_actions", intl),
				]
			: []),
	];

const makeGetTableRows =
	(
		availableGroups: UserGroup[],
		isAdministratorViewing: boolean,
	): TableWrapperProps<PaginatedUserList, AnyObject>["getRows"] =>
	(intl, dispatch, data, modalApi) => {
		return data.results.map((user: User, index: number) => {
			const showDeleteConfirm = () => {
				modalApi?.confirm({
					title: intl.formatMessage({ id: "user_delete_confirmation" }),
					icon: <ExclamationCircleFilled />,
					okText: intl.formatMessage({ id: "confirm" }),
					okType: "danger",
					cancelText: intl.formatMessage({ id: "cancel" }),
					onOk() {
						if (user.id) {
							dispatch(userManagementActions.deleteUser(user.id));
						}
					},
					onCancel() {},
				});
			};

			const onEdit = () => {
				dispatch(
					routerActions.navigateTo(routeNames.SETTINGS_USER_EDIT, {
						id: user.id,
					}),
				);
			};

			const customActions = user.is_active
				? [
						{
							key: "deactivate",
							icon: <MinusCircleOutlined />,
							tooltip: intl.formatMessage({ id: "user_action_deactivate" }),
							danger: true,
							onClick: () => {
								if (user.id) {
									dispatch(userManagementActions.deactivateUser(user.id));
								}
							},
						},
					]
				: [
						{
							key: "reactivate",
							icon: <RedoOutlined />,
							tooltip: intl.formatMessage({ id: "user_action_reactivate" }),
							onClick: () => {
								if (user.id) {
									dispatch(userManagementActions.reactivateUser(user.id));
								}
							},
						},
					];

			const groupName =
				availableGroups.find((g) => g.id === user.groups?.[0])?.name ?? null;

			const administratorCells = isAdministratorViewing
				? {
						is_active: (
							<Tag color={user.is_active ? "green" : "red"}>
								{intl.formatMessage({
									id: user.is_active ? "user_active" : "user_inactive",
								})}
							</Tag>
						),
						last_login: user.last_login
							? commonColumns.dateTimeValue(user.last_login, intl)
							: null,
						actions: commonColumns.actionsValue(
							undefined,
							undefined,
							undefined,
							undefined,
							undefined,
							onEdit,
							showDeleteConfirm,
							customActions,
							"User",
						),
					}
				: {};

      return {
        key: user.id,
        row_number: index + 1,
        username: (
          <Typography.Link
            onClick={() =>
              dispatch(
                routerActions.navigateTo(routeNames.USER_PROFILE_VIEW, { id: user.id })
              )
            }
          >
            {user.username}
          </Typography.Link>
        ),
        full_name: [user.first_name, user.last_name].filter(Boolean).join(" ") || "—",
        email: user.email,
        phone_number: user.phone_number ?? "—",
        technician_level: user.technician_level ? (
          <Tag color={TECHNICIAN_LEVEL_COLOR[user.technician_level]}>
            {intl.formatMessage({
              id: `user_technician_level_${user.technician_level.toLowerCase()}`,
            })}
          </Tag>
        ) : null,
        groups: groupName,
        ...administratorCells,
      };
    });
  };

type UsersPageProps = ReturnType<typeof withUsers> & DispatchProp;

export const UsersPage = (props: UsersPageProps) => {
	const {
		results,
		dispatch,
		currentRoute,
		intl,
		availableGroups,
		modalApi,
		currentUser,
	} = props;

	const [form] = Form.useForm();

	const isAdmin = isAdministrator(currentUser?.groups);

	if (!currentRoute) {
		return <Spin indicator={<LoadingOutlined spin />} size="large" />;
	}

	const pagination = results.pagination;

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({ id: "user_table_header_name" })}
				subtitle={intl.formatMessage(
					{ id: "user_table_header_number_of_items" },
					{ no: pagination.total_items },
				)}
				extra={
					isAdmin ? (
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() =>
								dispatch(
									routerActions.navigateTo(routeNames.SETTINGS_USER_CREATE),
								)
							}
						>
							{intl.formatMessage({ id: "user_create_button" })}
						</Button>
					) : undefined
				}
			>
				<Card
					title={
						<ListActionBar
							intl={intl}
							form={form}
							onFilter={onFilter(dispatch, form, currentRoute)}
							onCancel={onCancel(dispatch, form, currentRoute)}
							filterTemplate={userManagementFilter}
							currentFilter={currentRoute.params}
							searchBarPlaceholderKey={intl.formatMessage({
								id: "user_table_header_search_placeholder",
							})}
						>
							{isAdmin && (
								<DrawerItemWrapper>
									<Form.Item
										name="is_active"
										label={intl.formatMessage({ id: "user_is_active_filter" })}
									>
										<Select
											allowClear
											options={[
												{
													label: intl.formatMessage({ id: "user_active" }),
													value: "true",
												},
												{
													label: intl.formatMessage({ id: "user_inactive" }),
													value: "false",
												},
											]}
										/>
									</Form.Item>
								</DrawerItemWrapper>
							)}
							<DrawerItemWrapper>
								<Form.Item
									name="email"
									label={intl.formatMessage({ id: "table_column_email" })}
								>
									<Input allowClear />
								</Form.Item>
							</DrawerItemWrapper>
							<DrawerItemWrapper>
								<Form.Item
									name="technician_level"
									label={intl.formatMessage({ id: "user_technician_level" })}
								>
									<Select
										allowClear
										options={[
											{
												label: intl.formatMessage({ id: "user_technician_level_l1" }),
												value: "L1",
											},
											{
												label: intl.formatMessage({ id: "user_technician_level_l2" }),
												value: "L2",
											},
											{
												label: intl.formatMessage({ id: "user_technician_level_l3" }),
												value: "L3",
											},
										]}
									/>
								</Form.Item>
							</DrawerItemWrapper>
							<DrawerItemWrapper>
								<Form.Item
									name="groups__name"
									label={intl.formatMessage({ id: "user_group" })}
								>
									<Select
										allowClear
										showSearch
										options={availableGroups.map((g) => ({
											value: g.name,
											label: g.name,
										}))}
									/>
								</Form.Item>
							</DrawerItemWrapper>
						</ListActionBar>
					}
				>
					<TableWrapper
						data={results}
						getRows={makeGetTableRows(availableGroups, isAdmin)}
						getColumns={makeGetColumns(isAdmin)}
						intl={intl}
						route={currentRoute}
						modalApi={modalApi}
						applyFilterAction={(filterParams) =>
							routerActions.navigateTo(
								routeNames.SETTINGS_USER_LIST,
								filterParams,
							)
						}
						pagination={pagination as PaginationAndFilter}
					/>
				</Card>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
};

const withUsers = (state: RootState) => ({
	results: state.userManagement.list,
	currentRoute: state.router.route,
	intl: state.intl.datetimeIntl,
	availableGroups: state.userManagement.availableGroups,
	modalApi: state.appFeedback.modal,
	currentUser: state.userstate.user,
});

export const ConnectedUsersPage = connect(withUsers)(UsersPage);
export default ConnectedUsersPage;
